import React from "react";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";

export type DonutSegment = {
    label: string;
    value: number;
    color: string;
};

type Props = {
    segments: DonutSegment[];
    size?: number;
    thickness?: number;
    centerTop?: string;
    centerSub?: string;
    formatValue: (value: number) => string;
};

export const Donut: React.FC<Props> = ({
    segments,
    size = 168,
    thickness = 22,
    centerTop = "",
    centerSub = "",
    formatValue,
}) => {
    const total = segments.reduce((sum, segment) => sum + segment.value, 0);
    if (total <= 0) return null;

    const radius = (size - thickness) / 2;
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;

    return (
        <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ flexShrink: 0 }}>
                {segments.map((segment) => {
                    const fraction = segment.value / total;
                    const length = fraction * circumference;
                    const dashOffset = -offset;
                    offset += length;
                    return (
                        <circle
                            key={segment.label}
                            cx={cx}
                            cy={cy}
                            r={radius}
                            fill="none"
                            stroke={segment.color}
                            strokeWidth={thickness}
                            strokeDasharray={`${length.toFixed(2)} ${(circumference - length).toFixed(2)}`}
                            strokeDashoffset={dashOffset.toFixed(2)}
                            transform={`rotate(-90 ${cx} ${cy})`}
                            data-tip={`<b>${segment.label}</b><br>${(fraction * 100).toFixed(1)}% · ${formatValue(segment.value)}`}
                            style={{ cursor: "pointer" }}
                        />
                    );
                })}
                <text x={cx} y={cy - 2} textAnchor="middle" fill="#F6F7F9" fontSize={19} fontWeight={600}>
                    {centerTop}
                </text>
                <text
                    x={cx}
                    y={cy + 16}
                    textAnchor="middle"
                    fill={CHART_COLORS.axis}
                    fontSize={10.5}
                    fontWeight={500}
                    letterSpacing=".04em"
                >
                    {centerSub}
                </text>
            </svg>
            <div className="legend">
                {segments.map((segment) => (
                    <div
                        key={segment.label}
                        className="legend-row"
                        data-tip={`<b>${segment.label}</b><br>${((segment.value / total) * 100).toFixed(1)}%`}
                    >
                        <span className="legend-dot" style={{ background: segment.color }} />
                        <span className="legend-lab">{segment.label}</span>
                        <span className="legend-val">
                            {((segment.value / total) * 100).toFixed(0)}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};
